import React from 'react';
import { Input, RadioGroup, Radio, Checkbox, ModalContent, ModalBody } from '@heroui/react';
import { FilterSelect, ResponsiveModal } from '../../../../components/ui';
import { SearchInputIc, GlobalIc } from '../../../../components/icons';
import { getSortOptions, getProposalSources } from '../constants';
import { useTranslations } from '../../../../contexts/translation.context';
import { FiltersProps } from '../types';
import { CustomCheckbox, Typography } from '../../../../components';
import { useIsMobile } from '../../../../hooks/useMobile';

const FilterPopover: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ isOpen, onClose, children }) => {
  const isMobile = useIsMobile();
  if (!isOpen) return null;

  if (isMobile) {
    return (
      <ResponsiveModal isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
        <ModalContent>
          <ModalBody className="p-4 py-6">{children}</ModalBody>
        </ModalContent>
      </ResponsiveModal>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute top-full left-0 mt-2 z-50 bg-white border border-[#e0e0e0] rounded-xl p-4 shadow-lg min-w-[23.3125rem]">
        {children}
      </div>
    </>
  );
};

const FiltersSection: React.FC<FiltersProps> = ({
  filters,
  localSearch,
  sortDirection,
  onSearchChange,
  onFilterChange,
  onSortDirectionChange,
}) => {
  const { t } = useTranslations();
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  return (
    <div className="flex items-center gap-4 justify-start">
      <Input
        type="text"
        placeholder={t('proposals.searchPlaceholder')}
        size="sm"
        value={localSearch}
        onChange={(e) => onSearchChange(e.target.value)}
        endContent={<SearchInputIc />}
        classNames={{
          mainWrapper: 'w-[17.0625rem]',
          base: 'w-[17.0625rem]',
          input: 'text-sm',
          inputWrapper: 'rounded-lg h-9 w-[17.0625rem]',
        }}
      />

      <div className="relative">
        <button
          className="flex items-center gap-2 px-3 py-2 bg-auth-surface !rounded-2 h-9 min-w-[6.3125rem]"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M2.5 5.83333H6.25M6.25 5.83333C6.25 7.21405 7.36929 8.33333 8.75 8.33333C10.1307 8.33333 11.25 7.21405 11.25 5.83333M6.25 5.83333C6.25 4.45262 7.36929 3.33333 8.75 3.33333C10.1307 3.33333 11.25 4.45262 11.25 5.83333M11.25 5.83333H17.5M2.5 14.1667H8.75M8.75 14.1667C8.75 15.5474 9.86929 16.6667 11.25 16.6667C12.6307 16.6667 13.75 15.5474 13.75 14.1667M8.75 14.1667C8.75 12.786 9.86929 11.6667 11.25 11.6667C12.6307 11.6667 13.75 12.786 13.75 14.1667M13.75 14.1667H17.5"
              stroke="#a8a8b0"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <span className="text-sm text-auth-muted">{t('proposals.filter')}</span>
        </button>

        <FilterPopover isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)}>
          <div className="flex flex-col gap-6">
            {/* Price range */}
            <div>
              <label className="block text-sm text-auth-muted mb-3">{t('proposals.fanCost')}</label>
              <div className="flex items-center gap-3">
                <Input
                  type="text"
                  placeholder={t('proposals.from')}
                  value={filters.price_min?.toString() || ''}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9.]/g, '');
                    onFilterChange('price_min', val ? parseFloat(val) || null : null);
                  }}
                  startContent={
                    <Typography variant="body2" className="text-[#72727B]">
                      $
                    </Typography>
                  }
                  size="sm"
                  classNames={{
                    mainWrapper: 'w-[8rem] md:w-[9.4375rem]',

                    base: 'w-[8rem] md:w-[9.4375rem]',
                    input: 'text-sm',
                    inputWrapper: 'bg-auth-surface rounded-lg h-9 w-[8rem] md:w-[9.4375rem]',
                  }}
                />
                <span className="text-auth-muted">—</span>
                <Input
                  type="text"
                  placeholder={t('proposals.to')}
                  value={filters.price_max?.toString() || ''}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9.]/g, '');
                    onFilterChange('price_max', val ? parseFloat(val) || null : null);
                  }}
                  startContent={
                    <Typography variant="body2" className="text-[#72727B]">
                      $
                    </Typography>
                  }
                  size="sm"
                  classNames={{
                    mainWrapper: 'w-[8rem] md:w-[9.4375rem]',

                    base: 'w-[8rem] md:w-[9.4375rem]',
                    input: 'text-sm',
                    inputWrapper: 'bg-auth-surface rounded-lg h-9 w-[8rem] md:w-[9.4375rem]',
                  }}
                />
              </div>
            </div>

            {/* Offer type */}
            <div>
              <RadioGroup
                label={t('proposals.offerType')}
                value={filters.offer_type}
                onValueChange={(value: string) => onFilterChange('offer_type', value)}
                classNames={{
                  label: 'text-sm text-auth-muted',
                  wrapper: 'gap-2',
                }}
              >
                <Radio value="all">{t('proposals.all')}</Radio>
                <Radio value="cpl">{t('proposals.offerTypeCpl')}</Radio>
                <Radio value="subscription">{t('proposals.offerTypeSubscription')}</Radio>
              </RadioGroup>
            </div>

            {/* Campaign type */}
            <div>
              <RadioGroup
                label={t('proposals.campaignType')}
                value={filters.campaign_type}
                onValueChange={(value: string) => onFilterChange('campaign_type', value)}
                classNames={{
                  label: 'text-sm text-auth-muted',
                  wrapper: 'gap-2',
                }}
              >
                <Radio value="all">{t('proposals.all')}</Radio>
                <Radio value="free">{t('proposals.free')}</Radio>
                {/* <Radio value="paid">{t('proposals.paid')}</Radio> */}
                <Radio value="trial">{t('proposals.freeTrial')}</Radio>
              </RadioGroup>
            </div>

            {/* Partner level */}
            <div>
              <RadioGroup
                label={t('proposals.partnerLevel')}
                value={filters.partner_level}
                onValueChange={(value: string) => onFilterChange('partner_level', value)}
                classNames={{
                  label: 'text-sm text-auth-muted',
                  wrapper: 'gap-2',
                }}
              >
                <Radio value="all">{t('proposals.all')}</Radio>
                <Radio value="new">{t('proposals.newPartner')}</Radio>
                <Radio value="base">{t('proposals.basePartner')}</Radio>
                <Radio value="pro">{t('proposals.proPartner')}</Radio>
                <Radio value="expert">{t('proposals.expertPartner')}</Radio>
              </RadioGroup>
            </div>

            <div className="flex flex-col gap-2">
              <div>
                <Checkbox
                  classNames={{ base: '!h-6 py-0 ' }}
                  isSelected={filters.suitable}
                  onValueChange={(checked) => onFilterChange('suitable', checked)}
                >
                  {t('proposals.onlyAvailable')}
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  classNames={{ base: '!h-6 py-0 ' }}
                  isSelected={filters.featured}
                  onValueChange={(checked) => onFilterChange('featured', checked)}
                >
                  {t('proposals.onlyFavorites')}
                </Checkbox>
              </div>
            </div>
          </div>
        </FilterPopover>
      </div>

      <FilterSelect
        placeholder={t('proposals.sorting')}
        options={getSortOptions(t)}
        selectedKey={filters.sort}
        variant="campaign"
        onSelectionChange={(value) => onFilterChange('sort', value)}
        startContent={
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path
              fill="none"
              stroke="#A8A8B0"
              strokeLinecap="round"
              strokeWidth="1.5"
              d="M22 7H2m17 5H5m11 5H8"
            />
          </svg>
        }
        autoWidth={true}
        showPlaceholderWithValue={true}
        sortDirection={filters.sort !== 'all' ? sortDirection : null}
        onSortDirectionChange={onSortDirectionChange}
      />

      <FilterSelect
        placeholder={t('proposals.allSources')}
        options={getProposalSources(t)}
        selectedKey={filters.source}
        variant="campaign"
        onSelectionChange={(value) => onFilterChange('source', value)}
        startContent={<GlobalIc/>}
        autoWidth={true}
        showPlaceholderWithValue={true}
      />
    </div>
  );
};

export default FiltersSection;
